import { BaseEffect, EasingFunction, Easing, CanvasDimensions, EffectResult } from './BaseEffect';

/**
 * CircleCoverEffect creates a circular mask around the existing canvas content.
 * It uses a composite operation to mask the content into a circle shape.
 */
export class CircleCoverEffect extends BaseEffect {
    constructor(
        duration: number = 0,
        at?: number,
        easing: EasingFunction = Easing.linear
    ) {
        super('circleCover', duration, at, easing);
    }

    apply(
        ctx: CanvasRenderingContext2D,
        dimensions: CanvasDimensions,
        progress: number,
        prev: EffectResult
    ): EffectResult {
        // Calculate the center and radius
        const centerX = dimensions.resolutionWidth / 2;
        const centerY = dimensions.resolutionHeight / 2;
        const radius = Math.min(dimensions.resolutionWidth, dimensions.resolutionHeight) / 2;

        // Create a temporary canvas for the circle mask
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = dimensions.resolutionWidth;
        tempCanvas.height = dimensions.resolutionHeight;
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return prev;

        // Draw the circle mask
        tempCtx.fillStyle = 'black';
        tempCtx.beginPath();
        tempCtx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        tempCtx.fill();

        // Set up the composite operation for masking
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(tempCanvas, 0, 0);
        ctx.globalCompositeOperation = 'source-over';

        return {
            shape: 'circle',
            circle: { x: centerX, y: centerY, radius }
        };
    }

    getContainerStyle(
        progress: number,
        dimensions?: CanvasDimensions,
        effectResult?: EffectResult
    ): React.CSSProperties {
        if (!dimensions) return {};

        // Create a style that will trigger a canvas update
        return {
            '--circle-mask': 'true',
            width: '100%',
            height: '100%',
            position: 'relative'
        } as React.CSSProperties;
    }
} 